import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
// The Strict* form deliberately: the loose `CheckboxProps` adds
// `[key: string]: any`, which would re-admit `any` on every property read.
import type { DropdownProps, StrictCheckboxProps } from 'semantic-ui-react';
import {
  Checkbox,
  Icon,
  Loader,
  Popup,
  Table,
  Comment,
  Grid,
  Button,
  Form,
  Dropdown,
} from 'semantic-ui-react';
import type { FoodOrder, FoodRecommendationNote } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import * as Helpers from '../../../../utils/Helpers';
import { mapKey } from '../../../../utils/mapKey';
import { sortDirection } from '../../../../utils/sortDirection';

import { statusColors } from './constants';

/**
 * What the draft-orders table accepts.
 *
 * Established from its single JSX call site in `./index.tsx`.
 *
 * The handlers and `selectedOrderIds` are required: all are declared in that
 * parent's own component body (`useState`, `useReducer`, plain function
 * declarations), so they exist whenever the table is rendered. The rest stay
 * optional because the call site supplies them from its own optional props, or
 * because the reducer seeds `column`, `direction` and `data` as `null`.
 *
 * SUSPECTED DEFECT (table.tsx:370) — REPORTED, NOT FIXED. `props.doNotContact`
 * gates the `disabled` class on the alternate-food edit icon, but `./index.tsx`
 * does not pass it, so the guard never fires. This is the same gap as
 * `../orders/table.tsx:545`, against the same upstream flag
 * (`components/patient/index.tsx:51`), which suggests one dropped hand-off
 * rather than two independent oversights.
 *
 * Three props are the mirror case — passed by `./index.tsx` and read nowhere
 * here: `org` (line 794), `markAccepted` (line 804) and `patientObservations`
 * (line 807). `markAccepted` in particular is a bulk-accept action that this
 * table renders no control for; only the per-row `acceptSingleOrder` is wired.
 */
type DraftOrdersTableProps = {
  /** Accepts one draft order; the second argument is the status to move it to. */
  acceptSingleOrder: (order: FoodOrder, status: string) => void;
  /** Fires as each row's export checkbox toggles. */
  addExportData: (
    event: React.FormEvent<HTMLInputElement>,
    checkprops: StrictCheckboxProps,
    orderId?: string,
  ) => void;
  /** The currently sorted column key, or `null` before any sort. */
  column?: string | null;
  /** Rows to render. `null` until the parent's reducer receives its first dataset. */
  data?: FoodOrder[] | null;
  /**
   * Read but never passed — see the SUSPECTED DEFECT note above.
   * `unknown` rather than `boolean` because no producer establishes the type.
   */
  doNotContact?: unknown;
  /** Sort direction, forwarded to Semantic's `sorted`. */
  direction?: 'ascending' | 'descending' | null;
  /** The parent's `useReducer` dispatch; every call here is a `CHANGE_SORT`. */
  dispatch: (action: { type: string; sortcolumn?: string }) => void;
  /** Accepted but unread — see the type doc above. Shape follows the producer. */
  markAccepted?: (status: string, selectedOrderIdsTemp: (string | undefined)[]) => void;
  /** Opens the roster-wide member drawer from a row. */
  openSelectedPatient: (patientId?: string) => void;
  /** Accepted but unread — see the type doc above. */
  org?: string;
  /** Accepted but unread — see the type doc above. */
  patientObservations?: unknown;
  /**
   * Members keyed by id, used to label rows in the roster-wide mode.
   *
   * Open-keyed: the subscript is an order's own `patientId`, so a row naming a
   * member this map was not built for reads `undefined` — which is why the read
   * site tests `patients[id]` first and renders a loader otherwise.
   *
   * Declared by what this table READS — `firstName` and `lastName`, at the single
   * read site — rather than as a whole `FlatPatient`. The parent holds this state as
   * `Record<string, unknown>`, so demanding the full 23-field record here was a
   * requirement no caller met and no column needed. Both name fields are `unknown`
   * because `parseFHIRPatient` fills them from `fhirpath.evaluate`, which returns
   * arrays; the read site concatenates with `+`, which is total over both.
   */
  patients?: Record<string, { firstName?: unknown; lastName?: unknown } | undefined> | null;
  /** Absent selects the roster-wide mode. */
  patientId?: string | null;
  /** Header checkbox handler. */
  selectAll: (event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => void;
  /** Ids currently ticked. See the SUSPECTED DEFECT on `selectAll` in `./index.tsx`. */
  selectedOrderIds: (string | undefined)[];
  /** Swaps a draft onto one of its `alternateFoodOptions`. */
  updateAlternateOption: (
    order: FoodOrder,
    alternateFoodOptions: string[],
    selectedAlternate: string,
  ) => void;
  /** Moves a draft to another status; the reject control calls it with 'rejected'. */
  updateDraftOrder: (order: FoodOrder, status: string) => void;
  /** A vendor code, or the literal 'All'. */
  vendor?: string;
};

function DraftOrdersTable(props: DraftOrdersTableProps) {
  const _authInfo = useAuthContext();

  const [selectedAlternate, setSelectedAlternate] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [openedOrder, setOpenedOrder] = useState('');

  const handleChange = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedAlternate(value as string);
  };

  return (
    <>
      <div className="table-div">
        <Table sortable celled singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell content={<Checkbox onChange={props.selectAll} />} />
              <Table.HeaderCell width={1}>Order Id</Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'orderDate' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderDate' })}
              >
                Order Date
              </Table.HeaderCell>
              {!props.patientId && (
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(props.column === 'firstName' ? props.direction : null)}
                  onClick={() =>
                    props.dispatch({
                      type: 'CHANGE_SORT',
                      sortcolumn: `firstName`,
                    })
                  }
                >
                  {`Member Name`}
                </Table.HeaderCell>
              )}

              {
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(props.column === 'vendorId' ? props.direction : null)}
                  onClick={() =>
                    props.dispatch({
                      type: 'CHANGE_SORT',
                      sortcolumn: `vendorId`,
                    })
                  }
                >
                  {`Vendor`}
                </Table.HeaderCell>
              }

              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Program
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'foodTypeName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'foodTypeName',
                  })
                }
              >
                Food Vendor Pref
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'foodTypeName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'foodTypeName',
                  })
                }
              >
                Allergy
              </Table.HeaderCell>

              <Table.HeaderCell
                width={1}
                {...sortDirection(props.column === 'flavorName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'flavorName',
                  })
                }
              >
                Condition
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'quantity' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'quantity' })}
              >
                Food Preferences/Avoidances
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(props.column === 'orderDate' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderDate' })}
              >
                Recommended Food
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'createdBy' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'createdBy' })}
              >
                Alternate Options
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'sentDate' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'sentDate' })}
              >
                Accept/Reject
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {/*
              SUSPECTED DEFECT — REPORTED, NOT FIXED. The twin of the same read in
              `../orders/table.tsx`: `data` is `FoodOrder[] | null`, the parent's
              reducer seeds it `null`, and `.map` on `null` throws during render.
              The results-count footer at the bottom of this same file guards the
              identical value, so the omission is local to this read. It is masked
              today by the call site rendering the table only once a dataset has
              arrived.
            */}
            {props.data!.map((order) => {
              const orderStatus = order.status && order.status.toLowerCase();
              const colorCode = statusColors[mapKey(orderStatus)];

              // The subscripts below are four hard-coded rule names; a recommendation
              // carrying none of them misses, which is why each read is `?.`-guarded.
              const recommenderNotes: Record<string, FoodRecommendationNote | undefined> = {};
              order?.recommendation?.notes?.forEach((note) => {
                if (Object.prototype.hasOwnProperty.call(note, 'noteCode')) {
                  recommenderNotes[mapKey(note.noteCode)] = { ...note };
                }
              });

              const itemsContent = order.orderItems && order.orderItems.length > 0 && (
                <Grid key={order.orderId} centered>
                  <Grid.Row divided>
                    <Grid.Column floated="left">
                      <div
                        style={{
                          height: '300px',
                          overflowX: 'hidden',
                          overflowY: 'auto',
                        }}
                      ></div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              );
              const notesContent =
                order?.orderNotes &&
                Array.isArray(order?.orderNotes) &&
                order?.orderNotes?.length > 0
                  ? order?.orderNotes?.map((on) => {
                      return (
                        <Comment.Group>
                          <Comment>
                            <Comment.Avatar src={'../../../../images/' + Helpers.getImage(true)} />
                            <Comment.Content>
                              <Comment.Author as="a">{on.author}</Comment.Author>
                              <Comment.Metadata>{moment.utc(on.time).fromNow()}</Comment.Metadata>
                              <Comment.Text>{on.text}</Comment.Text>
                            </Comment.Content>
                          </Comment>
                        </Comment.Group>
                      );
                    })
                  : typeof order?.orderNotes === 'string'
                    ? order.orderNotes
                    : '';

              const _orderName = (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>{order.orderName}</div>&nbsp;&nbsp;&nbsp;&nbsp;
                  <div>
                    {order.orderNotes && order.orderNotes.length > 0 ? (
                      <Popup
                        on="click"
                        position="bottom right"
                        wide
                        trigger={<Icon name="sticky note" color="orange" link />}
                        content={notesContent}
                      />
                    ) : (
                      ''
                    )}
                    {itemsContent && (
                      <Popup
                        wide={order?.shippedItems ? 'very' : true}
                        on="click"
                        position="bottom right"
                        closeOnEscape
                        closeOnDocumentClick
                        content={itemsContent}
                        flowing
                        trigger={<Icon link name="clipboard list" />}
                      />
                    )}
                  </div>
                </div>
              );

              return (
                <Table.Row
                  positive={colorCode === 'yy'}
                  warning={colorCode === 'warning'}
                  negative={colorCode === 'negative'}
                  error={colorCode === 'error'}
                  style={{
                    backgroundColor:
                      (colorCode === 'blue' && '#F0F8FF') || (colorCode === 'yellow' && '#ffefd5'),
                    color: colorCode === 'zz' && '#808080',
                  }}
                >
                  <Table.Cell
                    width={1}
                    collapsing
                    content={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Checkbox
                          checked={props.selectedOrderIds.includes(order.orderId)}
                          onChange={(e, checkprops) =>
                            props.addExportData(e, checkprops, order.orderId)
                          }
                        />
                      </div>
                    }
                  />
                  <Table.Cell>{order.orderId}</Table.Cell>
                  <Table.Cell>
                    {order.orderDate
                      ? moment.utc(order.orderDate).local().format('YYYY-MM-DD hh:mm a')
                      : ''}
                  </Table.Cell>
                  {!props.patientId && (
                    <Table.Cell>
                      {props.patientId && props.vendor === 'All'
                        ? order.vendorId
                        : !props.patientId && (
                            <a
                              onClick={
                                !props.patientId
                                  ? (e) => {
                                      e.preventDefault();
                                      props.openSelectedPatient(order.patientId);
                                    }
                                  : (null as unknown as React.MouseEventHandler<HTMLAnchorElement>)
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              {props.patients && props.patients[order.patientId!] ? (
                                // Concatenation, not arithmetic: the operands coerce to string as
                                // upstream intends. Wrapping them in String() would emit a call.
                                /* eslint-disable @typescript-eslint/restrict-plus-operands */
                                props.patients[order.patientId!]!.firstName +
                                ' ' +
                                props.patients[order.patientId!]!.lastName
                              ) : (
                                /* eslint-enable @typescript-eslint/restrict-plus-operands */
                                <Loader size="tiny" active inline />
                              )}
                            </a>
                          )}
                    </Table.Cell>
                  )}
                  {<Table.Cell>{order.vendorId}</Table.Cell>}

                  <Table.Cell>{order?.patientOrg}</Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['vendor-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['vendor-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['vendor-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['allergy-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['allergy-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['allergy-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['medical-condition-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['medical-condition-rule']?.warningMessage !==
                        undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['medical-condition-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['food-preference-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['food-preference-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['food-preference-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {order.orderName}
                    {order?.alternateFoodOptions !== undefined &&
                      order?.alternateFoodOptions.length > 0 && (
                        <Popup
                          id={order?.orderId}
                          wide="very"
                          on="click"
                          position="top right"
                          closeOnEscape
                          hideOnScroll
                          closeOnDocumentClick
                          onClose={() => {
                            setIsOpen(false);
                          }}
                          trigger={
                            <Icon
                              name="edit"
                              link
                              color="green"
                              style={{ marginLeft: 5, fontSize: '1.2rem' }}
                              className={props.doNotContact ? 'disabled' : ''}
                              onClick={() => {
                                setIsOpen(true);
                                setOpenedOrder(order?.orderId as string);
                              }}
                            />
                          }
                          open={isOpen && order?.orderId === openedOrder}
                          content={
                            <>
                              <Form.Group widths="equal">
                                <Dropdown
                                  basic
                                  labeled
                                  selection
                                  search
                                  placeholder="SELECT ALTERNATE FOOD"
                                  autoComplete="off"
                                  selectOnBlur={false}
                                  name="edit"
                                  onChange={handleChange}
                                  required
                                  wrapSelection
                                  //    options={emailOptions}
                                  options={order?.alternateFoodOptions.map((x) => {
                                    return { key: x, text: x, value: x };
                                  })}
                                ></Dropdown>
                                <Button
                                  style={{ marginLeft: '4px' }}
                                  size="small"
                                  icon="edit outline"
                                  color="blue"
                                  onClick={() => {
                                    setIsOpen(false);
                                    setOpenedOrder('');
                                    props.updateAlternateOption(
                                      order,
                                      [
                                        ...(order?.alternateFoodOptions as string[]),
                                        order.orderName!,
                                      ],
                                      selectedAlternate,
                                    );
                                  }}
                                  content="Update Food Order"
                                />
                              </Form.Group>
                            </>
                          }
                        />
                      )}
                  </Table.Cell>

                  <Table.Cell>{order?.alternateFoodOptions?.join(', ')}</Table.Cell>

                  <Table.Cell>
                    <Button
                      size="small"
                      icon="handshake outline"
                      color="green"
                      onClick={() => props.acceptSingleOrder(order, 'new')}
                      content="Accept"
                    />
                    <Button
                      size="small"
                      icon="cancel"
                      color="red"
                      onClick={() => props.updateDraftOrder(order, 'rejected')}
                      content="Reject"
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <p style={{ margin: '25px 0 10px 15px', color: '#0067a5', fontSize: '17px' }}>
        Results Filtered: {props.data && props.data.length}
      </p>
    </>
  );
}

export default DraftOrdersTable;
